package com.evoapps.evoshift.ui.util

import com.evoapps.evoshift.util.usesDecimalComma
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.LabourBase
import com.evoapps.evoshift.domain.staffing.DayStaffingHint
import com.evoapps.evoshift.domain.staffing.ExpectedSalesSource
import com.evoapps.evoshift.domain.staffing.StaffingVerdict
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.ui.finance.formatPercent
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.staffing_hint_add_hours
import evoshift.composeapp.generated.resources.staffing_hint_cut_hours
import evoshift.composeapp.generated.resources.staffing_hint_from_forecast
import evoshift.composeapp.generated.resources.staffing_hint_from_recent
import evoshift.composeapp.generated.resources.staffing_hint_from_target
import evoshift.composeapp.generated.resources.staffing_hint_income
import evoshift.composeapp.generated.resources.staffing_hint_income_fixed
import evoshift.composeapp.generated.resources.staffing_hint_just_right
import evoshift.composeapp.generated.resources.staffing_hint_just_right_income
import evoshift.composeapp.generated.resources.staffing_hint_open_hours
import evoshift.composeapp.generated.resources.staffing_hint_open_estimated
import evoshift.composeapp.generated.resources.staffing_hint_unpriced
import evoshift.composeapp.generated.resources.staffing_hint_unpriced_people
import evoshift.composeapp.generated.resources.staffing_hint_income_sales_fixed
import evoshift.composeapp.generated.resources.staffing_hint_too_few
import evoshift.composeapp.generated.resources.staffing_hint_too_few_income
import evoshift.composeapp.generated.resources.staffing_hint_too_many
import evoshift.composeapp.generated.resources.staffing_hint_too_many_income
import evoshift.composeapp.generated.resources.staffing_just_right_short
import evoshift.composeapp.generated.resources.staffing_too_few_short
import evoshift.composeapp.generated.resources.staffing_too_many_short
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

/*
 * SH-54: the words for the staffing hints' facts (DayStaffingHint). The chip says the verdict
 * ("Too many"), the sentence says why ("Too many staff: labour 72% of expected income (target
 * 55%–65%)"; SH-95: "of expected sales" for a workplace without a commission), the details what
 * to change, where the expected sales come from and (with a commission) the income they bring.
 */

/** "Too few", "Just right", "Too many". */
fun staffingVerdictShortRes(verdict: StaffingVerdict): StringResource = when (verdict) {
    StaffingVerdict.TOO_FEW -> Res.string.staffing_too_few_short
    StaffingVerdict.JUST_RIGHT -> Res.string.staffing_just_right_short
    StaffingVerdict.TOO_MANY -> Res.string.staffing_too_many_short
}

fun staffingVerdictSentenceRes(verdict: StaffingVerdict, base: LabourBase = LabourBase.SALES): StringResource = when (base) {
    LabourBase.SALES -> when (verdict) {
        StaffingVerdict.TOO_FEW -> Res.string.staffing_hint_too_few
        StaffingVerdict.JUST_RIGHT -> Res.string.staffing_hint_just_right
        StaffingVerdict.TOO_MANY -> Res.string.staffing_hint_too_many
    }
    LabourBase.INCOME -> when (verdict) {
        StaffingVerdict.TOO_FEW -> Res.string.staffing_hint_too_few_income
        StaffingVerdict.JUST_RIGHT -> Res.string.staffing_hint_just_right_income
        StaffingVerdict.TOO_MANY -> Res.string.staffing_hint_too_many_income
    }
}

/** Amber for too few, red for too many, green when just right; lighter shades [onDark] backgrounds. */
fun staffingColor(verdict: StaffingVerdict, onDark: Boolean = false): Color = when (verdict) {
    StaffingVerdict.TOO_FEW -> if (onDark) Color(0xFFFFB74D) else Color(0xFFE65100)
    StaffingVerdict.JUST_RIGHT -> if (onDark) Color(0xFF81C784) else Color(0xFF2E7D32)
    StaffingVerdict.TOO_MANY -> if (onDark) Color(0xFFEF9A9A) else Color(0xFFC62828)
}

/** Hours as the hints round them: "9", "1.5" ("1,5" in Polish). */
fun formatHintHours(hours: Double, language: String = appLanguage()): String {
    val text = formatDecimal(abs(hours), 1, decimalSeparator = if (usesDecimalComma(language)) ',' else '.')
    return text.removeSuffix(if (usesDecimalComma(language)) ",0" else ".0")
}

/** The hint's sentence: the verdict, the labour % and the band. */
@Composable
fun staffingHintSentence(hint: DayStaffingHint): String = stringResource(
    staffingVerdictSentenceRes(hint.verdict, hint.base),
    formatPercent(hint.labourPercent),
    formatPercent(hint.target.low),
    formatPercent(hint.target.high)
)

/** What to change, open shifts not yet counted, and where the expected sales come from. */
@Composable
fun staffingHintDetails(hint: DayStaffingHint, currency: String, names: Map<String, String> = emptyMap()): List<String> = buildList {
    val hours = hint.hoursToBand
    if (hours != null && hours != 0.0) {
        add(stringResource(if (hours > 0) Res.string.staffing_hint_add_hours else Res.string.staffing_hint_cut_hours, formatHintHours(hours)))
    }
    if (hint.openHours > 0) {
        val openCost = hint.openCost
        add(
            if (openCost != null) stringResource(Res.string.staffing_hint_open_estimated, formatHintHours(hint.openHours), formatMoney(openCost, currency))
            else stringResource(Res.string.staffing_hint_open_hours, formatHintHours(hint.openHours))
        )
    }
    // Assigned shifts nobody could price: not hidden, and whose pay rates are missing.
    if (hint.unpricedShifts > 0) {
        add(stringResource(Res.string.staffing_hint_unpriced, hint.unpricedShifts, formatHintHours(hint.unpricedHours)))
        val people = hint.unpricedUserIds.mapNotNull { names[it] }
        if (people.isNotEmpty()) add(stringResource(Res.string.staffing_hint_unpriced_people, people.joinToString(", ")))
    }
    val sales = formatMoney(hint.expectedSales.amount, currency)
    add(
        when (hint.expectedSales.source) {
            ExpectedSalesSource.DAY_TARGET -> stringResource(Res.string.staffing_hint_from_target, sales)
            ExpectedSalesSource.FORECAST -> stringResource(Res.string.staffing_hint_from_forecast, sales)
            ExpectedSalesSource.RECENT_WEEKDAYS -> stringResource(Res.string.staffing_hint_from_recent, sales, hint.expectedSales.basedOnDays)
        }
    )
    // SH-95: what the labour % is measured against, with a commission.
    if (hint.base == LabourBase.INCOME) {
        val income = formatMoney(hint.expectedIncome, currency)
        val commission = formatPercent(hint.commissionPercent * 100)
        add(
            if (hint.commissionPercent <= 0) {
                // No commission: the whole net sales plus the fixed revenue.
                stringResource(Res.string.staffing_hint_income_sales_fixed, income, formatMoney(hint.fixedRevenue, currency))
            } else if (hint.fixedRevenue > 0) {
                stringResource(Res.string.staffing_hint_income_fixed, income, commission, formatMoney(hint.fixedRevenue, currency))
            } else {
                stringResource(Res.string.staffing_hint_income, income, commission)
            }
        )
    }
}

/** A small coloured chip with the verdict; screen readers and tests get the whole sentence. */
@Composable
fun StaffingChip(hint: DayStaffingHint, modifier: Modifier = Modifier) {
    val color = staffingColor(hint.verdict)
    val sentence = staffingHintSentence(hint)
    Text(
        stringResource(staffingVerdictShortRes(hint.verdict)),
        color = color,
        fontSize = 9.sp,
        lineHeight = 11.sp,
        fontWeight = FontWeight.Bold,
        maxLines = 1,
        modifier = modifier
            .clip(RoundedCornerShape(50))
            .background(color.copy(alpha = 0.14f))
            .padding(horizontal = 6.dp, vertical = 1.dp)
            .semantics { contentDescription = sentence }
    )
}

/** The hint in full: the coloured sentence, then the details in [color] (day sheets, the daily log's cards). */
@Composable
fun StaffingHintLines(
    hint: DayStaffingHint,
    currency: String,
    color: Color = MaterialTheme.colorScheme.onSurfaceVariant,
    onDark: Boolean = false,
    modifier: Modifier = Modifier,
    /** People's names by user id, to name those without a pay rate. */
    names: Map<String, String> = emptyMap()
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(2.dp)) {
        // SH-152: tap the sentence to see the labour % worked out with the day's numbers.
        com.evoapps.evoshift.ui.finance.ExplainableAmount(
            staffingHintSentence(hint),
            explain = { com.evoapps.evoshift.domain.explain.StaffingExplanations.labourPercent(hint) },
            currency = currency,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = staffingColor(hint.verdict, onDark)
        )
        staffingHintDetails(hint, currency, names).forEach { line ->
            Text(line, style = MaterialTheme.typography.bodySmall, color = color)
        }
    }
}

/**
 * The staffing hints of the shown month by ISO date (SH-54), for those who may see money: the
 * daily log's cards, rows and carousel read it, as they read [LocalDayClosures].
 */
val LocalStaffingHints = staticCompositionLocalOf<Map<String, DayStaffingHint>> { emptyMap() }
